import { ROLE_RANK, type Role } from '@support/shared';
import { create } from 'zustand';

import { authApi, errorMessage, setUnauthorizedHandler } from '@/lib/api';
import { queryClient } from '@/lib/query';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import type { ProjectAccess, UserProfile } from '@/lib/types';

/**
 * Кто вошёл, куда ему можно и какой проект открыт сейчас.
 *
 * Стор владеет ТОЛЬКО состоянием сессии. Транспорт токенов (cookie против
 * CloudStorage, single-flight обновление) живёт в lib/api — здесь про него
 * знать нечего, кроме факта «сессия есть / сессии нет».
 */

/** Выбранный проект переживает перезагрузку: оператор работает в одном и том же. */
const PROJECT_KEY = 'support_project';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthState {
  status: AuthStatus;
  user: UserProfile | null;
  /** Проекты с ролью В КАЖДОМ из них: глобальная роль их не заменяет. */
  projects: ProjectAccess[];
  currentProjectId: string | null;

  /** Восстановить сессию при загрузке вкладки. Идемпотентно. */
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setCurrentProject: (projectId: string | null) => void;
  /** Перечитать профиль: роли и список проектов могли поменяться. */
  reloadProfile: () => Promise<void>;
}

/**
 * Выбрать проект, который откроется по умолчанию.
 *
 * Сохранённый — если он всё ещё доступен (права могли снять). Иначе первый
 * активный: панель с пустым переключателем проектов выглядит как поломка.
 */
function pickProject(projects: ProjectAccess[], preferred: string | null): string | null {
  if (preferred && projects.some((project) => project.id === preferred)) return preferred;
  const active = projects.find((project) => project.isActive);
  return active?.id ?? projects[0]?.id ?? null;
}

/** Разложить ответ /auth/me в состояние стора и поднять сокет. */
function sessionFrom(user: UserProfile, projects: ProjectAccess[]): Partial<AuthState> {
  const currentProjectId = pickProject(projects, localStorage.getItem(PROJECT_KEY));
  if (currentProjectId) localStorage.setItem(PROJECT_KEY, currentProjectId);

  // Сокет поднимаем только с живым токеном: иначе рукопожатие отклонят, а
  // socket.io уйдёт в бесконечный цикл переподключений.
  connectSocket();
  return { status: 'authenticated', user, projects, currentProjectId };
}

const ANONYMOUS: Partial<AuthState> = {
  status: 'anonymous',
  user: null,
  projects: [],
  currentProjectId: null,
};

let bootstrapPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  projects: [],
  currentProjectId: null,

  bootstrap: () => {
    // React 19 в StrictMode монтирует эффекты дважды; без общего промиса это
    // два параллельных /auth/refresh, а ротация одноразовая — второй убил бы
    // только что выданную сессию.
    bootstrapPromise ??= (async () => {
      try {
        // Токена в памяти после перезагрузки нет: api поймает 401, обновится
        // по cookie (или по CloudStorage в Mini App) и повторит запрос.
        const { projects, ...user } = await authApi.me();
        set(sessionFrom(user, projects));
      } catch {
        // Сессии нет — это штатный путь первого захода, а не ошибка.
        set(ANONYMOUS);
      } finally {
        bootstrapPromise = null;
      }
    })();
    return bootstrapPromise;
  },

  login: async (email, password) => {
    // Ошибку не глотаем: форму логина интересует именно текст отказа.
    await authApi.login(email, password);
    try {
      const { projects, ...user } = await authApi.me();
      set(sessionFrom(user, projects));
    } catch (error) {
      // Токен уже выдан, а профиль не доехал: сессия поднята наполовину, и
      // панель без списка проектов бесполезна. Показываем внятный отказ.
      throw new Error(errorMessage(error, 'Не удалось загрузить профиль'));
    }
  },

  logout: async () => {
    // Порядок важен: сначала рвём сокет, потом гасим сессию на сервере.
    // Иначе ревалидация гейтвея успеет отключить сокет сама и залогировать
    // это как «учётка больше не активна».
    disconnectSocket();
    await authApi.logout();
    queryClient.clear();
    localStorage.removeItem(PROJECT_KEY);
    set(ANONYMOUS);
  },

  setCurrentProject: (projectId) => {
    if (projectId) localStorage.setItem(PROJECT_KEY, projectId);
    else localStorage.removeItem(PROJECT_KEY);
    set({ currentProjectId: projectId });
  },

  reloadProfile: async () => {
    const { projects, ...user } = await authApi.me();
    set({
      user,
      projects,
      // Права могли снять, пока вкладка была открыта: выбранный проект обязан
      // остаться в пределах доступных.
      currentProjectId: pickProject(projects, get().currentProjectId),
    });
  },
}));

/**
 * Что делать, когда обновление сессии окончательно провалилось.
 *
 * Ставится один раз на модуль: api не знает ни про роутер, ни про стор, а
 * жёсткий `location.assign` перезагрузил бы приложение и потерял черновик
 * в композере.
 */
setUnauthorizedHandler(() => {
  // Никто не входил — значит, и разлогинивать некого. Так выглядит холодный
  // старт без сессии: /auth/me отвечает 401, обновляться нечем, и это штатный
  // путь, а не потеря доступа. Сбрасывать здесь состояние значило бы гасить
  // ещё не начавшийся bootstrap.
  if (useAuthStore.getState().status !== 'authenticated') return;

  disconnectSocket();
  queryClient.clear();
  useAuthStore.setState(ANONYMOUS);
});

// ──────────────────────────── Селекторы ────────────────────────────

/** Проект, открытый сейчас. null — доступных проектов нет вовсе. */
export function useCurrentProject(): ProjectAccess | null {
  return useAuthStore((state) => {
    const id = state.currentProjectId;
    return id ? (state.projects.find((project) => project.id === id) ?? null) : null;
  });
}

export function useCurrentProjectId(): string | null {
  return useAuthStore((state) => state.currentProjectId);
}

export function useCurrentUser(): UserProfile | null {
  return useAuthStore((state) => state.user);
}

/** Роль в конкретном проекте. null — доступа нет. */
export function projectRole(projectId: string | null): Role | null {
  if (!projectId) return null;
  const { projects } = useAuthStore.getState();
  return projects.find((project) => project.id === projectId)?.role ?? null;
}

/**
 * Права «не ниже чем» в текущем проекте.
 *
 * Проверка на фронте — это про то, что показывать, а не про безопасность:
 * отказ всё равно выдаёт API. Прятать кнопку, которая гарантированно вернёт
 * 403, всё же обязательно — иначе оператор жмёт её и не понимает отказа.
 */
export function useHasRole(minimum: Role): boolean {
  return useAuthStore((state) => {
    const id = state.currentProjectId;
    const role = id ? state.projects.find((project) => project.id === id)?.role : undefined;
    // Глобальная роль важнее проектной: OWNER видит проекты без membership.
    const effective = role ?? state.user?.role;
    return effective ? ROLE_RANK[effective] >= ROLE_RANK[minimum] : false;
  });
}

/** Администратор платформы: ему доступен раздел /admin. */
export function useIsPlatformAdmin(): boolean {
  return useAuthStore((state) => {
    const role = state.user?.role;
    return role === 'OWNER' || role === 'ADMIN';
  });
}
